import { AppThunk } from 'store';
import { setConnecting, setLoading } from 'features/audio/audioSlice';

let abortController = new AbortController();
let fetchAudioTimeout: NodeJS.Timeout | undefined;
// Every loadAudio starts a new poll chain and invalidates the previous one.
// Retries carry the chain they belong to and bail out if a newer chain has
// started, so a stale chain can neither keep polling a dead URL nor hand its
// URL to the player after it has been replaced.
let currentChain = 0;

const fetchAudioUrl = async (
  src: string,
  audioType: string,
  setSrc: (_: string) => void,
  chain: number,
  retries = 0
) => {
  if (chain !== currentChain) return;

  if (audioType === 'recording') {
    setSrc(src);
    return;
  }

  abortController = new AbortController();
  const signal = abortController.signal;

  const scheduleRetry = () => {
    if (chain !== currentChain) return;
    if (retries >= 50) {
      setSrc(src);
      return;
    }
    const retryDelay = retries < 5 ? 2000 : 6000;
    fetchAudioTimeout = setTimeout(() => {
      console.log('[Audio] - Fetching audio: ', src);
      fetchAudioUrl(src, audioType, setSrc, chain, retries + 1);
    }, retryDelay);
  };

  try {
    const response = await fetch(src, { method: 'GET', signal });

    if (chain !== currentChain) return;

    if (!response.ok || !response.body) {
      scheduleRetry();
      return;
    }

    const reader = response.body.getReader();
    let hasData = false;
    const timeoutId = setTimeout(() => {
      reader.cancel().catch(() => undefined);
    }, 5000);

    try {
      const { value, done } = await reader.read();
      clearTimeout(timeoutId);
      hasData = !done && !!(value && value.length > 0);
    } catch {
      hasData = false;
    } finally {
      try {
        await reader.cancel();
      } catch {
        // ignore — already cancelled or stream closed
      }
    }

    if (chain !== currentChain) return;

    if (hasData) {
      setSrc(src);
    } else {
      scheduleRetry();
    }
  } catch (err) {
    if (err.name == 'AbortError') {
      console.log('[Audio] - Abort Error');
    } else {
      scheduleRetry();
    }
  }
};

const delay = (delayInms: number) => {
  return new Promise((resolve) => setTimeout(resolve, delayInms));
};

export const fetchAudioAbort = () => {
  console.log('[Audio] - fetchAudioAbort');
  currentChain++;
  if (fetchAudioTimeout) {
    clearTimeout(fetchAudioTimeout);
    fetchAudioTimeout = undefined;
  }
  if (!abortController.signal.aborted) {
    abortController.abort();
  }
};

export const checkAndPlayAudio =
  (
    src: string,
    audioType: string,
    setSrc: (_: string) => void,
    withDelay?: boolean
  ): AppThunk =>
  async (dispatch) => {
    fetchAudioAbort();
    const chain = currentChain;

    dispatch(setLoading(true));
    dispatch(setConnecting(true));
    await delay(withDelay ? 2000 : 0);
    await fetchAudioUrl(src, audioType, setSrc, chain);
  };
